import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconSpinner } from '../../components';

import css from './HeroLoadSpinner.module.css';

/**
 * Stable id for a still photo so a loaded hero is not spun again if the slide returns.
 *
 * @param {Object} image
 * @returns {string | null}
 */
export const stillImageKey = image => {
  if (!image) {
    return null;
  }
  if (image.id?.uuid) {
    return image.id.uuid;
  }
  if (typeof image.id === 'string') {
    return image.id;
  }
  return null;
};

/**
 * True when the current still has a URL for one of the variants the hero asks for.
 * An img with an empty srcset never fires load or error.
 *
 * @param {Object} image
 * @param {Array<string>} variants
 * @returns {boolean}
 */
export const stillHasRequestedVariant = (image, variants) =>
  (variants || []).some(name => image?.attributes?.variants?.[name]?.url);

/**
 * The browser does not replay a load event that fired before hydration.
 * A center hero that is already decoded should not keep the spinner up.
 *
 * @param {HTMLElement | null} root gallery root
 * @returns {boolean}
 */
export const currentHeroAlreadyLoaded = root => {
  if (!root) {
    return false;
  }
  return Array.from(root.querySelectorAll('.image-gallery-center img')).some(img => {
    const src = img.getAttribute('srcset') || img.getAttribute('src');
    return !!src && img.complete && img.naturalWidth > 0;
  });
};

/**
 * Spinner centered on the gallery stage while the current still photo loads.
 * It is portaled into the slide wrapper, which stays put while slides translate.
 *
 * @param {Object} props
 * @param {boolean} props.active
 * @param {React.RefObject<HTMLElement>} props.containerRef gallery root
 * @returns {React.ReactPortal | null}
 */
const HeroLoadSpinner = ({ active, containerRef }) => {
  const [stage, setStage] = useState(null);

  useEffect(() => {
    const root = containerRef.current;
    setStage(root ? root.querySelector('.image-gallery-slide-wrapper') : null);
  }, [containerRef, active]);

  if (!active || !stage) {
    return null;
  }

  return createPortal(
    <div className={css.root}>
      <IconSpinner />
    </div>,
    stage
  );
};

export default HeroLoadSpinner;
